import { Tooltip } from '@/components/tooltip';
import { FormLabel } from '@/components/ui/form';
import { formatInstant, FrequentTimeZones } from '@airreading/common';
import { SubstitutionRequest, SubstitutionRequestStatus } from '@airreading/common/data';
import { Info } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';

const DISPLAY_TIME_ZONE = FrequentTimeZones.UsCentral;

function formatSubstituteTimeTimestamp(instant: Temporal.Instant): string {
  const { formattedDateTime } = formatInstant(instant, DISPLAY_TIME_ZONE, undefined, ', ');
  return formattedDateTime;
}

function formatSubstitutionRequestTimestamp(instant: Temporal.Instant): string {
  const { formattedDateTime } = formatInstant(instant, DISPLAY_TIME_ZONE, undefined, ', ');
  return `[${formattedDateTime}]`;
}

export function SubstituteTimeValueDisplay({ request, testId }: { request: SubstitutionRequest; testId?: string }) {
  const requestedAt = formatSubstituteTimeTimestamp(request.createdAtInstant);
  const fulfilledAt =
    request.status === SubstitutionRequestStatus.fulfilled && request.acceptedAtInstant
      ? formatSubstituteTimeTimestamp(request.acceptedAtInstant)
      : null;

  return (
    <div className="flex flex-col gap-1 text-xs text-gray-500" data-testid={testId}>
      <span>Requested at: {requestedAt}</span>
      {fulfilledAt ? <span>Fulfilled at: {fulfilledAt}</span> : null}
    </div>
  );
}

function buildSubstitutionRequestHistoryLines(request: SubstitutionRequest): string[] {
  const requestedTimestamp = formatSubstitutionRequestTimestamp(request.createdAtInstant);
  const requesterName = request.requestedByCoach.fullNameOrEmail ?? 'Unknown teacher';
  const lines = [`${requestedTimestamp} Requested by ${requesterName}`];

  if (request.status === SubstitutionRequestStatus.pending) {
    return lines;
  }

  if (request.status === SubstitutionRequestStatus.cancelled) {
    const cancelledTimestamp = request.canceledAtInstant
      ? formatSubstitutionRequestTimestamp(request.canceledAtInstant)
      : '[unknown]';
    lines.push(`${cancelledTimestamp} The pending request was cancelled`);
    return lines;
  }

  if (!request.acceptedAtInstant) {
    return lines;
  }

  const fulfilledTimestamp = formatSubstitutionRequestTimestamp(request.acceptedAtInstant);
  const fulfillerName = request.substituteCoach?.fullNameOrEmail ?? 'Unknown teacher';
  lines.push(`${fulfilledTimestamp} Fulfilled by ${fulfillerName}`);
  return lines;
}

export function SubstitutionRequestHistoryTooltipContent({
  substitutionRequests,
}: {
  substitutionRequests: SubstitutionRequest[];
}) {
  const sortedRequests = [...substitutionRequests].sort((a, b) =>
    Temporal.Instant.compare(a.createdAtInstant, b.createdAtInstant),
  );

  return (
    <div className="space-y-3 text-sm font-normal">
      {sortedRequests.map((request) => {
        const lines = buildSubstitutionRequestHistoryLines(request);
        return (
          <div key={request.id} className="space-y-1">
            {lines.map((line, index) => (
              <p key={index} className="whitespace-nowrap font-normal">
                {line}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function SubstituteTimeHistoryTooltip({
  substitutionRequests,
  triggerTestId,
}: {
  substitutionRequests: SubstitutionRequest[];
  triggerTestId?: string;
}) {
  if (substitutionRequests.length === 0) {
    return null;
  }

  return (
    <Tooltip
      trigger={<Info className="h-5 w-5" data-testid={triggerTestId} />}
      contentClassName="w-max max-w-none font-normal"
    >
      <SubstitutionRequestHistoryTooltipContent substitutionRequests={substitutionRequests} />
    </Tooltip>
  );
}

export function SubstituteTimeLabelWithHistoryTooltip({
  substitutionRequests,
}: {
  substitutionRequests: SubstitutionRequest[];
}) {
  return (
    <div className="flex items-center gap-2">
      <FormLabel className="font-bold">Substitute time</FormLabel>
      <SubstituteTimeHistoryTooltip substitutionRequests={substitutionRequests} />
    </div>
  );
}
